import {
  searchCustomerCode,
  searchHubWithrealFilter,
  searchShelfNumber,
} from '@api/dashboard';
import { tmsBagExceptionSearch } from '@api/TMSDashboard/genericTMSView';
import { searchFilteredRiderCode } from '@api/genericAnalyticsView';
import { searchZone } from '@api/genericConsignmentView';
import { getAllVehicleList } from 'src/api/trips';
import { getSearchedVendorDetails } from 'src/api/vendorPayout';
import { tmsSetupSearchRoutes } from '@api/TMSDashboard/Setup';
import { getAllTmsGatesSearch, getAllTmsDocksSearch } from 'src/api/TMSDashboard/VehicleView';
import moment from 'moment';
import { fetchMoveableAssets } from 'src/api/retailDashboard';

export const filterFormItemLayout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};
export const filterTailLayout = {
  wrapperCol: { span: 24 },
};
export const defaultTokenSeperators = [',', ' '];

export const isInvalidFilter = (value) => {
  if (
    value === null ||
    value === undefined ||
    ((typeof value === 'string' || Array.isArray(value)) && !value.length) ||
    (value instanceof Object && !Object.keys(value).length)
  ) {
    return true;
  }
  return false;
};
export const sanitiseObject = (filters) => {
  const obj = { ...filters };
  for (const i in obj) {
    if (isInvalidFilter(obj[i])) {
      delete obj[i];
    }
  }
  return obj;
};

const customerCodeSearch = async (value) => {
  const response = await searchCustomerCode(value);
  let customerCodes = response.data;
  customerCodes = customerCodes
    ? customerCodes.map((cc: any) => ({
        key: cc.id,
        label: cc.name + ', ' + cc.code,
        data: cc,
      }))
    : [];
  return customerCodes;
};

const shelfNumberSearch = async (value) => {
  const response = await searchShelfNumber(value);
  let shelfNumbers = response.data;
  shelfNumbers = shelfNumbers
    ? shelfNumbers.map((cc: any) => ({
        key: cc.id,
        label: cc.shelfnumber,
        data: cc,
      }))
    : [];
  return shelfNumbers;
};

const exceptionSearch = async (value) => {
  const response = await tmsBagExceptionSearch(value);
  let exceptions = response.data;
  exceptions = exceptions
    ? exceptions.map((cc: any) => ({
        key: cc.bag_exception_id,
        label: `${cc.bag_exception_name} (${cc.bag_exception_code})`,
        data: cc,
      }))
    : [];
  return exceptions;
};

const hubIdSearch = (strategy) => async (value) => {
  const response =
    strategy === 'store_hub_search'
      ? await searchHubWithrealFilter(true)(value, 'upcountry')
      : await searchHubWithrealFilter(true)(value);
  let hubIdList = response.data;
  hubIdList = hubIdList
    ? hubIdList.map((hub: any) => ({
        key: hub.id,
        label: `${hub.name}, ${hub.code}`,
        data: hub,
      }))
    : [];
  return hubIdList;
};

const riderCodeSearch = async (value, hubIds?: any) => {
  const response = await searchFilteredRiderCode(value, hubIds);
  const riderCodes = response.data
    ? response.data.map((rider) => {
        return { key: rider.id, label: `${rider.name}, ${rider.code}`, data: rider };
      })
    : [];
  return riderCodes;
};

const zoneCodeSearch = async (value) => {
  const params = { q: value };
  const response = await searchZone(params);
  let zoneCodes = response.data;
  zoneCodes = zoneCodes
    ? zoneCodes.map((zone: any) => ({
        key: zone.node_code,
        label: `${zone.node_name}, ${zone.node_code}`,
        data: zone,
      }))
    : [];
  return zoneCodes;
};

const vehicleCodeSearch = async (value, hubId) => {
  const response = await getAllVehicleList(hubId, value);
  const vehicleCodes = response.data
    ? response.data.map((vehicle) => {
        return { key: vehicle.vehicle_id, label: `${vehicle.vehicle_number}, ${vehicle.vehicle_code}`, data: vehicle };
      })
    : [];
  return vehicleCodes;
};

const vehicleVendorSearch = async (value) => {
  const response = await getSearchedVendorDetails({ str: value });
  const vehicleVendors = response.data
    ? response.data.map((vendor) => {
      return { key: vendor.id, label: `${vendor.name}, ${vendor.code}`, data: vendor };
      })
    : [];
  return vehicleVendors;
};

const routeCodeSearch = async (value) => {
  const response = await tmsSetupSearchRoutes(value);
  const routes = response.data
    ? response.data.map((route) => {
        return {key: route.id, label: `${route.name}, ${route.code}` };
      })
    : [];
  return routes;
};

const gateCodeSearch = async (value, hubId) => {
  const body = {
    hub_id_list: [hubId],
    gate_search_string: value,
  };
  const response = await getAllTmsGatesSearch(body);
  const gateCodes = response?.data?.page_data
    ? response.data.page_data.map((gate) => {
        return { key: gate.gate_id, label: `${gate.gate_name}, ${gate.gate_code}` };
      })
    : [];
  return gateCodes;
};

const dockCodeSearch = async (value, hubId) => {
  const body = {
    hub_id_list: [hubId],
    docking_station_search_string: value,
  };
  const response = await getAllTmsDocksSearch(body);
  const dockCodes = response?.data?.page_data
    ? response.data.page_data.map((dock) => {
        return { key: dock.dock_id, label: `${dock.dock_name}, ${dock.dock_code}` };
      })
    : [];
  return dockCodes;
};

const moveableAssetSearch = async (value) => {
  const response = await fetchMoveableAssets({ code: value });
  const moveableAssets = response.data
    ? response.data.map((moveableAsset) => {
      return { key: moveableAsset.id, label: moveableAsset.code, data: moveableAsset };
      })
    : [];
  return moveableAssets;
};

const defaultFunction = (value) => {
  return [];
};

export const searchStrategy = (strategy, searchSelectKey?: string) => {
  const baseFn = (() => {
    switch (strategy) {
      case 'hub_search':
        return hubIdSearch(strategy);
      case 'store_hub_search':
        return hubIdSearch(strategy);
      case 'exception_search':
        return exceptionSearch;
      case 'customer_code_search':
        return customerCodeSearch;
      case 'rider_code_search':
        return riderCodeSearch;
      case 'zone_code_search':
        return zoneCodeSearch;
      case 'shelf_search':
        return shelfNumberSearch;
      case 'vehicle_code_search':
        return vehicleCodeSearch;
      case 'trip_vendor_search':
        return vehicleVendorSearch;
    case 'route_code_search':
      return routeCodeSearch;
    case 'gate_code_search':
      return gateCodeSearch;
    case 'dock_code_search':
      return dockCodeSearch;
      case 'moveable_asset_search':
        return moveableAssetSearch;
      default:
        return defaultFunction;
    }
  })();

  // Wrapper to transform results
  return async (...args: any[]) => {
    const results = await baseFn(...args);

    if (!searchSelectKey) {
      // Return original key/label without data field
      return results.map((item: any) => ({
        key: item.key,
        label: item.label,
      }));
    }

    // Transform using searchSelectKey from raw data
    return results.map((item: any) => {
      const keyValue = item.data?.[searchSelectKey] || item.key;
      return {
        key: keyValue,
        label: keyValue,
      };
    });
  };
};

// Function to format value based on metadata type
const formatFilterValueByType = (value: any, type: string, mode?: string) => {
  if (!value) return value;

  switch (type) {
    case 'search':
      if (Array.isArray(value)) {
        return value.map(item => item?.key || item);
      }
      return value?.key || value;
    
    case 'select':
      if (Array.isArray(value)) {
        return value.map(item => item?.value || item);
      }
      return value?.value || value;
    
    case 'datetime':
      // For datetime type, convert timestamp array to unix format
      if (Array.isArray(value) && value.length === 2) {
        return value.map(timestamp => moment(timestamp)?.valueOf());
      }
    
    default:
      // For other types (radio, input, inputnumber), return value as is
      return value;
  }
};

// Generic function to generate request schema from filter metadata
export const generateFiltersPayload = async (appliedFilters, filtersMetadata) => {
  if (!Array.isArray(filtersMetadata) || !filtersMetadata.length) {
    return {};
  }

  const formattedBody = {};

  filtersMetadata.forEach((field: any) => {
    const { id, type, mode } = field;
    
    if (appliedFilters.hasOwnProperty(id)) {
      formattedBody[id] = formatFilterValueByType(appliedFilters[id], type, mode);
    }
  });

  return formattedBody;
};
